// SAD-455: competing source observations for one graph entity. Observations are
// immutable evidence; a reviewer can keep them distinct, choose one, or enter a
// corrected value — always with a rationale, after seeing the consequence — and
// can later reverse that decision. Originals are never changed.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { isPermissionDenied, useObservationConflicts, useResolveObservationConflict, useReverseObservationResolution } from './conflictHooks';
import { EnrichmentControl } from './EnrichmentControl';
const OUTCOME_LABEL = {
    keep_distinct: 'Kept distinct',
    choose_observation: 'Chose an observation',
    correct_value: 'Corrected value'
};
function fmtTime(ts) {
    return ts ? ts.toDate().toLocaleString() : '—';
}
function period(start, end) {
    if (!start && !end) return 'unspecified period';
    return `${start || '…'} → ${end || '…'}`;
}
function conflictKey(c) {
    return [
        c.field,
        c.scope,
        c.measure,
        c.periodStart,
        c.periodEnd
    ].join('|');
}
function conflictTitle(c) {
    return [
        c.field,
        c.scope && `scope ${c.scope}`,
        c.measure && `measure ${c.measure}`
    ].filter(Boolean).join(' · ');
}
/** The standing decision: last resolution that is neither a reversal nor reversed. */ function activeResolution(c) {
    return [
        ...c.resolutions
    ].reverse().find((r)=>!r.reversesId && !r.reversedById);
}
const EMPTY_DRAFT = {
    outcome: 'keep_distinct',
    chosenObservationId: '',
    correctedValue: '',
    rationale: '',
    confirming: false
};
export function ObservationConflictsPanel({ nodeId, nodeName }) {
    const conflicts = useObservationConflicts(nodeId);
    if (conflicts.isError && isPermissionDenied(conflicts.error)) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-xs text-muted-foreground",
            "data-testid": Testids.sourceConflicts.accessDenied,
            children: "You don't have access to source conflicts for this entity."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.sourceConflicts.panelRoot,
        children: [
            /*#__PURE__*/ _jsx(EnrichmentControl, {
                nodeId: nodeId
            }),
            !!conflicts.data?.length && /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-medium text-muted-foreground",
                children: "Source review"
            }),
            conflicts.data?.map((c)=>/*#__PURE__*/ _jsx(ConflictRow, {
                    conflict: c,
                    nodeId: nodeId,
                    nodeName: nodeName
                }, conflictKey(c)))
        ]
    });
}
function ConflictRow({ conflict: c, nodeId, nodeName }) {
    const resolve = useResolveObservationConflict();
    const [draft, setDraft] = useState(null);
    const active = activeResolution(c);
    const patch = (p)=>setDraft((d)=>d ? {
                ...d,
                ...p
            } : d);
    const chosen = c.observations.find((o)=>o.id === draft?.chosenObservationId);
    const where = `${nodeName} · ${conflictTitle(c)} · ${period(c.periodStart, c.periodEnd)}`;
    const draftReady = !!draft && draft.rationale.trim() !== '' && (draft.outcome !== 'choose_observation' || !!chosen) && (draft.outcome !== 'correct_value' || draft.correctedValue.trim() !== '');
    const consequence = !draft ? '' : draft.outcome === 'keep_distinct' ? `Keep distinct: no observation is chosen over another for ${where}. Each stays as its own source reading.` : draft.outcome === 'choose_observation' ? `"${chosen?.value}" (${chosen?.source}, record ${chosen?.sourceRecordId}) becomes the preferred value for ${where}.` : `"${draft.correctedValue}" becomes the preferred value for ${where}, as a reviewed correction.`;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded-md border bg-muted/20 p-3 text-sm",
        "data-testid": Testids.sourceConflicts.conflictRow,
        "data-status": c.status,
        "data-field": c.field,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        children: [
                            conflictTitle(c),
                            ' ',
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-normal text-muted-foreground",
                                children: period(c.periodStart, c.periodEnd)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: c.status === 'open' ? 'outline' : 'secondary',
                        children: c.status
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs",
                children: [
                    "Preferred: ",
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: c.preferredValue || '—'
                    }),
                    ' ',
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground",
                        children: [
                            "(",
                            active ? `reviewed decision — ${OUTCOME_LABEL[active.outcome] ?? active.outcome}` : 'source precedence',
                            ")"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: c.observations.map((o)=>/*#__PURE__*/ _jsx(ObservationRow, {
                        obs: o,
                        preferred: o.id === c.preferredObservationId,
                        onChoose: draft && !draft.confirming ? ()=>patch({
                                outcome: 'choose_observation',
                                chosenObservationId: o.id
                            }) : undefined
                    }, o.id))
            }),
            !draft && /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "outline",
                "data-testid": Testids.sourceConflicts.resolveButton,
                onClick: ()=>setDraft({
                        ...EMPTY_DRAFT
                    }),
                children: "Resolve"
            }),
            draft && !draft.confirming && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border bg-background p-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-2",
                        children: [
                            [
                                'keep_distinct',
                                'correct_value'
                            ].map((o)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: draft.outcome === o ? 'default' : 'outline',
                                    "data-testid": Testids.sourceConflicts.outcomeOption,
                                    "data-outcome": o,
                                    onClick: ()=>patch({
                                            outcome: o
                                        }),
                                    children: o === 'keep_distinct' ? 'Keep distinct' : 'Correct value'
                                }, o)),
                            /*#__PURE__*/ _jsx("span", {
                                className: "self-center text-xs text-muted-foreground",
                                children: "or choose an observation above"
                            })
                        ]
                    }),
                    draft.outcome === 'correct_value' && /*#__PURE__*/ _jsx(SpecraInput, {
                        value: draft.correctedValue,
                        onChange: (e)=>patch({
                                correctedValue: e.target.value
                            }),
                        placeholder: "Corrected value",
                        "aria-label": "Corrected value",
                        "data-testid": Testids.sourceConflicts.correctedValueInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: draft.rationale,
                        onChange: (e)=>patch({
                                rationale: e.target.value
                            }),
                        placeholder: "Why you made this decision",
                        "aria-label": "Rationale",
                        "data-testid": Testids.sourceConflicts.rationaleInput
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !draftReady,
                                "data-testid": Testids.sourceConflicts.reviewButton,
                                onClick: ()=>patch({
                                        confirming: true
                                    }),
                                children: "Review"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setDraft(null),
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            }),
            draft?.confirming && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border bg-background p-2",
                "data-testid": Testids.sourceConflicts.confirmPanel,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs",
                        children: consequence
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            "Original observations are retained. Rationale: ",
                            draft.rationale
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: resolve.isPending,
                                "data-testid": Testids.sourceConflicts.confirmButton,
                                onClick: ()=>resolve.mutate({
                                        nodeId,
                                        field: c.field,
                                        scope: c.scope,
                                        measure: c.measure,
                                        periodStart: c.periodStart,
                                        periodEnd: c.periodEnd,
                                        outcome: draft.outcome,
                                        chosenObservationId: draft.outcome === 'choose_observation' ? draft.chosenObservationId : '',
                                        correctedValue: draft.outcome === 'correct_value' ? draft.correctedValue : '',
                                        rationale: draft.rationale.trim()
                                    }, {
                                        onSuccess: ()=>setDraft(null)
                                    }),
                                children: "Confirm resolution"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>patch({
                                        confirming: false
                                    }),
                                children: "Back"
                            })
                        ]
                    })
                ]
            }),
            c.resolutions.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 border-t pt-2",
                children: c.resolutions.map((r)=>/*#__PURE__*/ _jsx(HistoryRow, {
                        res: r,
                        nodeId: nodeId
                    }, r.id))
            })
        ]
    });
}
function ObservationRow({ obs: o, preferred, onChoose }) {
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex items-start justify-between gap-2 rounded border bg-background px-2 py-1 text-xs",
        "data-testid": Testids.sourceConflicts.observationRow,
        "data-observation-id": o.id,
        children: [
            /*#__PURE__*/ _jsxs("span", {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: o.value
                    }),
                    preferred && /*#__PURE__*/ _jsx(SpecraBadge, {
                        className: "ml-1",
                        children: "preferred"
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "block text-muted-foreground",
                        children: [
                            o.source,
                            " · record ",
                            o.sourceRecordId,
                            o.confidence ? ` · ${Math.round(o.confidence * 100)}% confidence` : ' · confidence unavailable',
                            o.sourceRecordVersion ? ` v${o.sourceRecordVersion}` : '',
                            " ·",
                            ' ',
                            period(o.periodStart, o.periodEnd),
                            " · observed ",
                            fmtTime(o.observedAt)
                        ]
                    })
                ]
            }),
            onChoose && /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "outline",
                "data-testid": Testids.sourceConflicts.chooseObservationButton,
                onClick: onChoose,
                children: "Choose this observation"
            })
        ]
    });
}
function HistoryRow({ res: r, nodeId }) {
    const reverse = useReverseObservationResolution();
    const [rationale, setRationale] = useState(null);
    const isReversal = !!r.reversesId;
    const reversed = !!r.reversedById;
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-1 text-xs",
        "data-testid": Testids.sourceConflicts.historyRow,
        "data-resolution-id": r.id,
        "data-reversed": reversed ? 'true' : 'false',
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            isReversal ? 'Reversal' : OUTCOME_LABEL[r.outcome] ?? r.outcome,
                            " by ",
                            r.actorUserId,
                            " ·",
                            ' ',
                            fmtTime(r.createdAt),
                            reversed && /*#__PURE__*/ _jsx(SpecraBadge, {
                                className: "ml-1",
                                children: "reversed"
                            })
                        ]
                    }),
                    !isReversal && !reversed && rationale === null && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": Testids.sourceConflicts.reverseButton,
                        onClick: ()=>setRationale(''),
                        children: "Reverse"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-muted-foreground",
                children: [
                    "Rationale: ",
                    r.rationale
                ]
            }),
            rationale !== null && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Reversing restores the preferred value to what applied before this decision. The original decision and observations are kept."
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: rationale,
                        onChange: (e)=>setRationale(e.target.value),
                        placeholder: "Why you are reversing it",
                        "aria-label": "Reversal rationale",
                        "data-testid": Testids.sourceConflicts.reverseRationaleInput
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !rationale.trim() || reverse.isPending,
                                "data-testid": Testids.sourceConflicts.reverseConfirmButton,
                                onClick: ()=>reverse.mutate({
                                        nodeId,
                                        resolutionId: r.id,
                                        rationale: rationale.trim()
                                    }, {
                                        onSuccess: ()=>setRationale(null)
                                    }),
                                children: "Confirm reversal"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setRationale(null),
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
